import { useState, useRef, useEffect } from "react";
import { AsYouType, CountryCode } from "libphonenumber-js";
import "./PhoneInput.css";
import { COUNTRY_OPTIONS_FULL } from "../utils/dialCodes";

const PRIORITY_CODES = ["US", "CA", "IE"];

const WAITLIST_COUNTRY_OPTIONS = [
  ...COUNTRY_OPTIONS_FULL.filter((c) => PRIORITY_CODES.includes(c.code)),
  ...COUNTRY_OPTIONS_FULL.filter((c) => !PRIORITY_CODES.includes(c.code)),
];

type PhoneInputProps = {
  phone: string; // E.164 format
  setPhone: (value: string) => void;
};

export default function PhoneInput({ phone, setPhone }: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<{
    name: string;
    dial_code: string;
    code: CountryCode;
  }>(WAITLIST_COUNTRY_OPTIONS[0] as any);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [rawDigits, setRawDigits] = useState("");
  const [displayValue, setDisplayValue] = useState(""); // formatted for UI

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!phone) {
      setRawDigits("");
      setDisplayValue("");
      return;
    }

    // Extract digits after country code
    const dial = selectedCountry.dial_code.replace(/\D/g, "");
    let digits = phone.replace(/^\+/, "");

    if (digits.startsWith(dial)) {
      digits = digits.slice(dial.length);
    }

    digits = digits.replace(/\D/g, "");

    setRawDigits(digits);

    const formatted = formatDisplay(digits, selectedCountry.code);
    setDisplayValue(formatted);
  }, [phone, selectedCountry]);

  useEffect(() => {
    if (!phone) return;

    const match = WAITLIST_COUNTRY_OPTIONS.find((c) =>
      phone.startsWith(c.dial_code),
    );

    if (match) {
      setSelectedCountry(match as any);
    }
  }, [phone]);

  const formatDisplay = (digits: string, country: CountryCode) => {
    const formatter = new AsYouType(country);
    return formatter.input(digits);
  };

  const handlePhoneChange = (input: string) => {
    const digits = input.replace(/\D/g, "").slice(0, 12);
    setRawDigits(digits);

    // Display value (national formatting)
    const formatted = formatDisplay(digits, selectedCountry.code);
    setDisplayValue(formatted);

    // E.164 value (for Twilio)
    const e164 = digits
      ? `+${selectedCountry.dial_code.replace(/\D/g, "")}${digits}`
      : "";
    setPhone(e164);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["ArrowLeft", "ArrowRight", "Tab", "Home", "End"].includes(e.key))
      return;

    if (e.key === "Backspace" || e.key === "Delete") {
      e.preventDefault();
      if (rawDigits.length > 0) {
        const newDigits =
          e.key === "Backspace" ? rawDigits.slice(0, -1) : rawDigits.slice(1);
        setRawDigits(newDigits);
        const formatted = formatDisplay(newDigits, selectedCountry.code);
        setDisplayValue(formatted);

        const e164 = newDigits
          ? `+${selectedCountry.dial_code.replace(/\D/g, "")}${newDigits}`
          : "";
        setPhone(e164);
      }
      return;
    }

    if (!/^\d$/.test(e.key)) e.preventDefault();
  };

  const handleCountryChange = (country: typeof selectedCountry) => {
    setSelectedCountry(country);
    setDropdownOpen(false);
    setRawDigits("");
    setDisplayValue("");
    setPhone("");
  };

  return (
    <div ref={wrapperRef} className="waitlist-phone-wrapper">
      <div className="custom-select" onClick={() => setDropdownOpen((o) => !o)}>
        {`${selectedCountry.code} ${selectedCountry.dial_code}`}
      </div>

      {dropdownOpen && (
        <ul className="custom-select-options">
          {WAITLIST_COUNTRY_OPTIONS.map((c) => (
            <li key={c.code} onClick={() => handleCountryChange(c as any)}>
              {c.name} {c.dial_code}
            </li>
          ))}
        </ul>
      )}

      <input
        type="tel"
        className="waitlist-phone-number"
        value={displayValue}
        onChange={(e) => handlePhoneChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Phone number"
      />
    </div>
  );
}
